/**
 * Displays source video frames (Neural Storyboard) frame by frame,
 * synced to the predicted neural activation peaks.
 *
 * Frames arrive as:  [ { t: <timepoint_index>, url: <CloudFront_URL> }, … ]
 * stored in results.tribe_v2_brain_frames
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, Brain, Cpu, ChevronLeft, ChevronRight, Zap } from 'lucide-react';

// Colour strip that mimics the "fire" colormap used in TRIBE v2's PlotBrain
const FIRE_GRADIENT = 'linear-gradient(90deg, #05060A 0%, #1a0533 12%, #6b0f6b 28%, #c83232 48%, #f7922e 70%, #ffd700 88%, #fffde7 100%)';

function PendingState() {
  return (
    <div
      className="flex flex-col items-center justify-center"
      style={{
        minHeight: 360,
        background: 'radial-gradient(ellipse at 50% 60%, rgba(255,255,255,0.03) 0%, transparent 70%)',
        border: '1px dashed rgba(255,255,255,0.08)',
        borderRadius: 16,
        padding: 40,
      }}
    >
      <div className="relative mb-6">
        {/* Pulsing brain icon */}
        <motion.div
          animate={{ scale: [1, 1.08, 1], opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            width: 72, height: 72, borderRadius: '50%',
            background: 'rgba(255,255,255,0.04)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Brain size={32} style={{ color: 'rgba(255,255,255,0.3)' }} />
        </motion.div>
        {/* Orbit ring */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
          style={{
            position: 'absolute', inset: -8,
            border: '1.5px dashed rgba(255,255,255,0.1)',
            borderRadius: '50%',
          }}
        />
      </div>

      <p className="text-sm font-semibold mb-2" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-0)' }}>
        SYNCING NEURAL STORYBOARD…
      </p>
      <p className="text-xs text-center leading-relaxed" style={{ color: 'var(--text-2)', maxWidth: 320 }}>
        Frames are being extracted and aligned to the neural timeline.
        Once GPU processing starts, this view updates live with frame-by-frame signal activity.
      </p>

      {/* Colormap preview */}
      <div className="mt-5 flex flex-col items-center gap-1.5">
        <div style={{
          width: 220, height: 10, borderRadius: 6,
          background: FIRE_GRADIENT,
          boxShadow: '0 0 14px rgba(248,120,42,0.35)',
        }} />
        <div className="flex justify-between w-full" style={{ width: 220 }}>
          <span className="text-xs" style={{ color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: 9 }}>LOW</span>
          <span className="text-xs" style={{ color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: 9 }}>HIGH ACTIVATION</span>
        </div>
        <p className="text-xs" style={{ color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.08em' }}>
          TRIBE V2 SIGNAL MAP
        </p>
      </div>
    </div>
  );
}

export default function NeuralStoryboard({ 
  frames = [], 
  currentTime, 
  duration, 
  nTimesteps, 
  nVertices,
  videoUrl,
  onTimeChange,
  onDurationChange
}) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [manualOverride, setManualOverride] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const filmstripRef = useRef(null);
  const videoRef = useRef(null);

  // Sync external currentTime into video player if it deviates significantly
  useEffect(() => {
    if (videoRef.current && !manualOverride) {
      if (Math.abs(videoRef.current.currentTime - currentTime) > 0.5) {
        videoRef.current.currentTime = currentTime;
      }
    }
  }, [currentTime, manualOverride]);
  const playTimerRef = useRef(null);

  // Sort frames by timepoint index
  const sortedFrames = [...frames].sort((a, b) => a.t - b.t);

  // Find closest frame to currentTime (as fraction of duration)
  const findClosestFrame = useCallback((ct) => {
    if (!sortedFrames.length) return 0;
    const targetT = (ct / Math.max(duration, 1)) * (nTimesteps || sortedFrames.length);
    let best = 0, bestDist = Infinity;
    sortedFrames.forEach((f, i) => {
      const d = Math.abs(f.t - targetT);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    return best;
  }, [sortedFrames, duration, nTimesteps]);

  // Sync to external timeline scrubber (only when not manually overriding)
  useEffect(() => {
    if (!manualOverride && !isPlaying && sortedFrames.length > 0) {
      setActiveIdx(findClosestFrame(currentTime));
    }
  }, [currentTime, manualOverride, isPlaying, findClosestFrame, sortedFrames.length]);

  // Auto-play logic — only drives the clock when there is NO video.
  // When a video is present the video element's onTimeUpdate is the clock master;
  // the setInterval would race it and corrupt the shared currentTime.
  const isFilmstripMode = !videoUrl || videoError;
  useEffect(() => {
    if (!isPlaying || !sortedFrames.length || !isFilmstripMode) return;
    playTimerRef.current = setInterval(() => {
      setActiveIdx((prev) => {
        const next = (prev + 1) % sortedFrames.length;
        const tFrac = sortedFrames[next].t / Math.max(nTimesteps || sortedFrames.length, 1);
        onTimeChange?.(tFrac * duration);
        return next;
      });
    }, 250); // 4 fps cinema playback (filmstrip-only)
    return () => clearInterval(playTimerRef.current);
  }, [isPlaying, sortedFrames, duration, nTimesteps, onTimeChange, isFilmstripMode]);

  // Scroll filmstrip to keep active thumb visible
  useEffect(() => {
    if (!filmstripRef.current || !sortedFrames.length) return;
    const thumbWidth = 56 + 4; // width + gap
    const thumbLeft = activeIdx * thumbWidth;
    const strip = filmstripRef.current;
    const stripWidth = strip.clientWidth;
    const scrollLeft = strip.scrollLeft;
    if (thumbLeft < scrollLeft + 8) {
      strip.scrollTo({ left: thumbLeft - 8, behavior: 'smooth' });
    } else if (thumbLeft + thumbWidth > scrollLeft + stripWidth - 8) {
      strip.scrollTo({ left: thumbLeft + thumbWidth - stripWidth + 8, behavior: 'smooth' });
    }
  }, [activeIdx, sortedFrames.length]);

  const goTo = (idx, emitTime = true) => {
    setManualOverride(true);
    setActiveIdx(idx);
    if (emitTime && sortedFrames[idx]) {
      const tFrac = sortedFrames[idx].t / Math.max(nTimesteps || sortedFrames.length, 1);
      const computedTime = tFrac * duration;
      onTimeChange?.(computedTime);
      if (videoRef.current) {
        videoRef.current.currentTime = computedTime;
      }
    }
    // Automatically release override after 5s so parent scrubber can resume control
    setTimeout(() => setManualOverride(false), 5000);
  };

  const step = (dir) => {
    goTo(Math.max(0, Math.min(sortedFrames.length - 1, activeIdx + dir)));
  };

  if (!sortedFrames.length) return <PendingState />;

  const active = sortedFrames[activeIdx];
  const tSeconds = nTimesteps > 0
    ? Math.round((active.t / nTimesteps) * duration)
    : active.t;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div style={{
            width: 28, height: 28, borderRadius: 8,
            background: 'rgba(248,120,42,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Brain size={14} style={{ color: '#f8782a' }} />
          </div>
          <div>
            <p className="text-xs font-semibold" style={{ color: 'var(--text-0)', fontFamily: 'var(--font-display)' }}>
              TRIBE v2 Neural Storyboard
            </p>
            <p className="text-xs" style={{ color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>
              {sortedFrames.length} frames · {nVertices?.toLocaleString() || '~20k'} vertices · T4 GPU processed
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#2EF2A5', boxShadow: '0 0 6px #2EF2A5' }} />
          <span style={{ fontSize: 10, color: '#2EF2A5', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em' }}>
            VISUAL-NEURAL SYNC · LIVE
          </span>
        </div>
      </div>

      {/* ── Main Frame Display ── */}
      <div style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', background: '#05060A' }}>
        {videoUrl && !videoError ? (
          <video
            ref={videoRef}
            src={videoUrl}
            controls
            playsInline
            controlsList="nodownload noplaybackrate"
            className="w-full"
            style={{ maxHeight: 420, objectFit: 'contain', outline: 'none' }}
            onTimeUpdate={(e) => {
              // Video is clock master — always propagate current time
              onTimeChange?.(e.target.currentTime);
            }}
            onSeeked={(e) => {
              // User dragged scrubber — sync immediately
              onTimeChange?.(e.target.currentTime);
            }}
            onLoadedMetadata={(e) => {
              if (e.target.duration > 0 && e.target.duration !== Infinity) {
                onDurationChange?.(e.target.duration);
              }
            }}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onError={() => setVideoError(true)}
          />
        ) : (
          <AnimatePresence mode="wait">
            <motion.img
              key={activeIdx}
              src={active.url}
              alt={`Storyboard frame t=${active.t}`}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.02 }}
              transition={{ duration: 0.12 }}
              style={{
                width: '100%',
                display: 'block',
                objectFit: 'contain',
                background: '#05060A',
                maxHeight: 420,
              }}
            />
          </AnimatePresence>
        )}

        {/* Time overlay */}
        <div style={{
          position: 'absolute', top: 10, left: 12,
          background: 'rgba(5,6,10,0.72)',
          borderRadius: 6, padding: '3px 8px',
          backdropFilter: 'blur(6px)',
          border: '1px solid rgba(248,120,42,0.25)',
        }}>
          <span style={{ fontSize: 10, color: '#f8782a', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em' }}>
            t = {tSeconds}s · frame {activeIdx + 1}/{sortedFrames.length}
          </span>
        </div>

        {/* TRIBE v2 badge */}
        <div style={{
          position: 'absolute', top: 10, right: 12,
          background: 'rgba(5,6,10,0.72)',
          borderRadius: 6, padding: '3px 8px',
          backdropFilter: 'blur(6px)',
          border: '1px solid rgba(255,255,255,0.1)',
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          <Cpu size={9} style={{ color: 'rgba(255,255,255,0.4)' }} />
          <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em' }}>
            T4 GPU
          </span>
        </div>

        {/* Nav arrows */}
        <button
          onClick={() => step(-1)}
          style={{
            position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
            background: 'rgba(5,6,10,0.6)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '50%', width: 30, height: 30,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', backdropFilter: 'blur(4px)',
          }}
          disabled={activeIdx === 0}
        >
          <ChevronLeft size={14} style={{ color: '#fff' }} />
        </button>
        <button
          onClick={() => step(1)}
          style={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
            background: 'rgba(5,6,10,0.6)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '50%', width: 30, height: 30,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', backdropFilter: 'blur(4px)',
          }}
          disabled={activeIdx === sortedFrames.length - 1}
        >
          <ChevronRight size={14} style={{ color: '#fff' }} />
        </button>
      </div>

      {/* ── Playback Controls ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button
          onClick={() => setIsPlaying(p => !p)}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '5px 12px', borderRadius: 8,
            background: isPlaying ? 'rgba(248,120,42,0.12)' : 'rgba(248,120,42,0.08)',
            border: `1px solid rgba(248,120,42,${isPlaying ? 0.4 : 0.2})`,
            cursor: 'pointer', color: '#f8782a',
            fontSize: 11, fontFamily: 'var(--font-mono)', letterSpacing: '0.06em',
            transition: 'all 0.15s',
          }}
        >
          {isPlaying ? <Pause size={11} /> : <Play size={11} />}
          {isPlaying ? 'PAUSE' : 'PLAY CINEMA'}
        </button>

        {/* Progress bar */}
        <div
          style={{
            flex: 1, height: 4, borderRadius: 4,
            background: 'rgba(255,255,255,0.07)',
            position: 'relative', cursor: 'pointer',
          }}
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const pct = (e.clientX - rect.left) / rect.width;
            goTo(Math.round(pct * (sortedFrames.length - 1)));
          }}
        >
          <div style={{
            position: 'absolute', left: 0, top: 0, bottom: 0,
            width: `${(activeIdx / Math.max(sortedFrames.length - 1, 1)) * 100}%`,
            background: FIRE_GRADIENT,
            borderRadius: 4,
            boxShadow: '0 0 8px rgba(248,120,42,0.5)',
            transition: 'width 0.1s',
          }} />
        </div>

        <span style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
          {activeIdx + 1} / {sortedFrames.length}
        </span>
      </div>

      {/* ── Filmstrip ── */}
      <div
        ref={filmstripRef}
        style={{
          display: 'flex', gap: 4, overflowX: 'auto', paddingBottom: 4,
          scrollbarWidth: 'none',
        }}
      >
        {sortedFrames.map((f, i) => (
          <button
            key={f.t}
            onClick={() => goTo(i)}
            style={{
              flex: '0 0 auto',
              width: 52, height: 38, padding: 0,
              border: i === activeIdx
                ? '1.5px solid #f8782a'
                : '1.5px solid rgba(255,255,255,0.08)',
              borderRadius: 6, overflow: 'hidden',
              cursor: 'pointer',
              background: '#05060A',
              transition: 'border-color 0.12s',
              boxShadow: i === activeIdx ? '0 0 8px rgba(248,120,42,0.45)' : 'none',
              opacity: i === activeIdx ? 1 : 0.6,
            }}
          >
            <img
              src={f.url}
              alt={`t${f.t}`}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </button>
        ))}
      </div>

      {/* ── Colormap legend ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={{
          height: 6, borderRadius: 4,
          background: FIRE_GRADIENT,
          boxShadow: '0 0 8px rgba(248,120,42,0.2)',
        }} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>LOW ACTIVATION</span>
          <span style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', letterSpacing: '0.06em' }}>A/B neural signal</span>
          <span style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>HIGH ACTIVATION</span>
        </div>
      </div>

      {/* ── What you're seeing ── */}
      <div style={{
        padding: '8px 12px', borderRadius: 8,
        background: 'rgba(248,120,42,0.05)',
        border: '1px solid rgba(248,120,42,0.12)',
      }}>
        <div className="flex items-center gap-2 mb-1">
          <Zap size={10} style={{ color: '#f8782a' }} />
          <span style={{ fontSize: 10, color: '#f8782a', fontFamily: 'var(--font-mono)', fontWeight: 600, letterSpacing: '0.06em' }}>
            WHAT YOU'RE SEEING
          </span>
        </div>
        <p style={{ fontSize: 11, color: 'var(--text-2)', lineHeight: 1.6 }}>
          This storyboard anchors predicted activation levels to actual frames of your ad.
          Each frame marks the moment that triggered the neural response shown above.
        </p>
      </div>
    </div>
  );
}
